import { useFetchOwnerDetails, userMapSelector } from '@repo/config-services';
import { type UsersType } from '@repo/config-services';
import { getUserFullNameByFL } from '@repo/utils';
import { Select, type SelectProps } from 'antd';
import {
  useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';

import styles from './OwnerSelectDropdown.module.scss';

function RenderOption(
  {
    email,
    firstName,
    lastName,
  }: {
    email: string;
    firstName: string;
    lastName: string;
  },
) {
  return (
    <div>
      <div>{getUserFullNameByFL(firstName, lastName) || 'No Name'}</div>
      <div>{email || 'No Email'}</div>
    </div>
  );
}

export default function OwnerSelectDropdown({
  multiple,
  value,
  handleClick,
  allowClear = true,
  disabled,
  forceFetch,
  optionValueProp = 'userUuid',
  className,
  withAnyOption,
  withCurrentUserOption,
  currentUser,
  selectProps,
  customFilter,
  placeholder = 'Select an Owner',
}: {
  multiple?: boolean;
  allowClear?:boolean;
  value?: string | string[];
  handleClick?: (value: string | string[]) => void;
  disabled?: boolean;
  forceFetch?: boolean;
  optionValueProp?: keyof UsersType;
  className?: string;
  withAnyOption?: boolean;
  withCurrentUserOption?: boolean;
  currentUser?: Partial<UsersType>;
  selectProps?: SelectProps;
  customFilter?: (options: any[]) => any[];
  placeholder?: string;
}) {
  const [focused, setFocused] = useState(forceFetch ?? false);
  const [searchText, setSearchText] = useState('');
  const [defferedSearchText, setDefferedSearchText] = useState('');
  const userMap = useSelector(userMapSelector);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDefferedSearchText(searchText);
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [searchText]);

  const {
    data,
    isFetching,
  } = useFetchOwnerDetails(defferedSearchText, {
    skip: !focused,
  });

  const options = useMemo(
    () => data?.users?.map((user) => ({
      value: user[optionValueProp],
      label: <RenderOption email={user.email} firstName={user.firstName} lastName={user.lastName} />,
      disabled,
      // Backend supports searching by email, First Name, Last Name individually
      title: `${user.email} ${user.lastName} ${user.firstName}`.trim(),
    })) || [],
    [data?.users, disabled, optionValueProp],
  );

  useEffect(() => {
    // prefetch options if value is present & !options
    if (value && value !== 'any' && !options.length) {
      setFocused(true);
    }
  }, [value, options]);

  const finalOptions = useMemo(() => {
    const preparedOptions = [...(options || [])];

    if (withCurrentUserOption) {
      preparedOptions.unshift({
        value: currentUser?.[optionValueProp] ?? '',
        label: <span>Current User</span>,
        title: 'Current User',
        disabled: false,
      });
    }

    if (withAnyOption) {
      preparedOptions.unshift({
        value: 'any',
        label: <span>Any</span>,
        title: 'Any',
        disabled: false,
      });
    }

    let filteredOptions = preparedOptions;
    if (customFilter) filteredOptions = customFilter(preparedOptions);
    return filteredOptions;
  }, [options, withCurrentUserOption, withAnyOption, customFilter, currentUser, optionValueProp]);

  return (
    <Select
      mode={multiple ? 'multiple' : undefined}
      showSearch
      loading={isFetching}
      searchValue={searchText}
      placeholder={placeholder}
      optionFilterProp="title"
      onSearch={setSearchText}
      options={finalOptions}
      allowClear={allowClear}
      value={value}
      labelRender={(val) => {
        const user = userMap[val.value];
        // @ts-ignore - Value has a title property but not in the type
        return user?.fullName ?? val?.title ?? '';
      }}
      onChange={(val) => {
        handleClick?.(val);
        setSearchText('');
      }}
      onFocus={() => setFocused(true)}
      className={className}
      disabled={disabled}
      rootClassName={styles.container}
      {...selectProps}
    />
  );
}
